import { Component } from "@theme/component";
import { loadSwiper } from "@theme/swiper-loader";

/**
 * @typedef {Object} ResourceListCarouselRefs
 * @property {HTMLButtonElement} [previous] - Previous slide button
 * @property {HTMLButtonElement} [next] - Next slide button
 * @property {HTMLElement} [pagination] - Segmented pagination bar
 */

/** @extends {Component<ResourceListCarouselRefs>} */
class ResourceListCarousel extends Component {
  /** @type {any} */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroy();
  }

  updatedCallback() {
    this.#destroy();
    this.#init();
  }

  async #init() {
    const swiperEl = this.querySelector("[data-resource-list-swiper]");

    if (!(swiperEl instanceof HTMLElement)) {
      return;
    }

    try {
      await loadSwiper();
    } catch {
      return;
    }

    if (typeof window.Swiper === "undefined") {
      return;
    }

    const slides = swiperEl.querySelectorAll(".swiper-slide");

    if (slides.length === 0) {
      return;
    }

    this.#destroy();

    const infinite = this.dataset.infinite === "true";
    const spaceBetween = this.#getSpaceBetween();
    const slidesOffsetBefore = this.#getGutterOffset();
    const slidesOffsetAfter = this.#getGutterOffset();
    const { previous, next, pagination } = this.refs;

    /** @type {Record<string, unknown>} */
    const options = {
      direction: "horizontal",
      slidesPerView: 1.2,
      spaceBetween,
      speed: 500,
      watchOverflow: true,
      grabCursor: true,
      keyboard: { enabled: true, onlyInViewport: true },
      slidesOffsetBefore,
      slidesOffsetAfter,
      breakpoints: {
        750: {
          slidesPerView: 2.2,
        },
        990: {
          slidesPerView: 3.2,
        },
      },
    };

    if (
      previous instanceof HTMLButtonElement &&
      next instanceof HTMLButtonElement
    ) {
      options.navigation = {
        prevEl: previous,
        nextEl: next,
        disabledClass: "slideshow-control--disabled",
      };
    }

    if (infinite && slides.length > 1) {
      options.loop = true;

      if (slides.length > 2) {
        options.loopAdditionalSlides = 2;
      }
    }

    if (pagination instanceof HTMLElement && slides.length > 1) {
      options.pagination = {
        el: pagination,
        type: "bullets",
        clickable: true,
        bulletClass: "swiper-pagination-bullet",
        bulletActiveClass: "swiper-pagination-bullet-active",
        renderBullet: (index, className) =>
          `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`,
      };
    }

    this.#swiper = new window.Swiper(swiperEl, options);
  }

  #getSpaceBetween() {
    if (this.dataset.spaceBetween !== undefined) {
      const fromData = Number.parseFloat(this.dataset.spaceBetween);

      if (Number.isFinite(fromData)) {
        return fromData;
      }
    }

    const resourceList = this.closest(".resource-list");
    const source = resourceList instanceof HTMLElement ? resourceList : this;

    for (const property of [
      "--resource-list-column-gap-desktop",
      "--resource-list-column-gap",
    ]) {
      const parsed = this.#resolveCssLength(source, property);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  }

  /**
   * Resolve a CSS custom property to a pixel length.
   * Handles fixed values (`20px`) and functional ones (`var()`, `min()`, `clamp()`)
   * that `parseFloat(getPropertyValue(...))` cannot read.
   *
   * @param {HTMLElement} source
   * @param {string} property
   * @returns {number}
   */
  #resolveCssLength(source, property) {
    const raw = getComputedStyle(source).getPropertyValue(property).trim();

    if (!raw) {
      return NaN;
    }

    if (!/var\(|min\(|max\(|clamp\(|calc\(/i.test(raw)) {
      const direct = Number.parseFloat(raw);
      return Number.isFinite(direct) ? direct : NaN;
    }

    const probe = document.createElement("div");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:absolute;visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden;";
    probe.style.setProperty("margin-left", `var(${property})`);
    source.appendChild(probe);
    const resolved = Number.parseFloat(getComputedStyle(probe).marginLeft);
    probe.remove();

    return Number.isFinite(resolved) ? resolved : NaN;
  }

  #getGutterOffset() {
    const gutter = getComputedStyle(this)
      .getPropertyValue("--gutter-slide-width")
      .trim();
    const parsed = Number.parseFloat(gutter);

    return Number.isFinite(parsed) ? parsed : 0;
  }

  #destroy() {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
  }
}

if (!customElements.get("resource-list-carousel")) {
  customElements.define("resource-list-carousel", ResourceListCarousel);
}
